import React, { useState, useEffect } from 'react';
import { regulatoryMatrix } from '../data/medical-device-database';
import Analytics from '../utils/analytics';

// SafeguardsAssessment component for evaluating administrative, physical, and technical safeguards
export default function SafeguardsAssessment({ 
  adminAnswers, 
  setAdminAnswers, 
  physicalAnswers, 
  setPhysicalAnswers, 
  technicalAnswers, 
  setTechnicalAnswers,
  adminScore,
  physicalScore,
  technicalScore,
  device
}) {
  const [activeSection, setActiveSection] = useState('administrative');
  const [expandedRequirement, setExpandedRequirement] = useState(null);
  
  // Set default values based on device selection if provided
  useEffect(() => {
    if (device) {
      // If device has predefined safeguard scores, we could pre-populate answers
      // This is a placeholder for that functionality
      console.log("Device selected:", device.name);
    }
  }, [device]);

  // Handle admin answer changes
  const handleAdminAnswerChange = (question, value) => {
    setAdminAnswers({
      ...adminAnswers,
      [question]: value
    });
    
    // Track the admin safeguard change
    Analytics.trackEvent('Safeguards Assessment', 
      'Administrative Safeguard Change', 
      `${question}: ${value}`
    );
  };
  
  // Handle physical answer changes
  const handlePhysicalAnswerChange = (question, value) => {
    setPhysicalAnswers({
      ...physicalAnswers,
      [question]: value
    });
    
    // Track the physical safeguard change
    Analytics.trackEvent('Safeguards Assessment', 
      'Physical Safeguard Change', 
      `${question}: ${value}`
    );
  };
  
  // Handle technical answer changes
  const handleTechnicalAnswerChange = (question, value) => {
    setTechnicalAnswers({
      ...technicalAnswers,
      [question]: value
    });
    
    // Track the technical safeguard change
    Analytics.trackEvent('Safeguards Assessment', 
      'Technical Safeguard Change', 
      `${question}: ${value}`
    );
  };

  // Get color based on score
  const getScoreColor = (score) => {
    if (score >= 8) return 'bg-green-100 text-green-800';
    if (score >= 6) return 'bg-blue-100 text-blue-800';
    if (score >= 4) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  // Get tooltip text based on score
  const getScoreTooltip = (score) => {
    if (score >= 8) return 'Strong safeguards';
    if (score >= 6) return 'Adequate safeguards';
    if (score >= 4) return 'Needs improvement';
    return 'Significant gaps';
  };

  // HIPAA citation for each safeguard question
  const CITATIONS = {
    securityRiskProcess:            '§164.308(a)(1)(ii)(A)',
    riskManagementPlan:             '§164.308(a)(1)(ii)(B)',
    securityIncidentDocumentation:  '§164.308(a)(6)',
    authorizationProcedures:        '§164.308(a)(3)(ii)(A)',
    accessTerminationProcess:       '§164.308(a)(3)(ii)(C)',
    accessAuthorizationProcess:     '§164.308(a)(4)(ii)(B)',
    roleBasedAccess:                '§164.308(a)(4)',
    staffTraining:                  '§164.308(a)(5)',
    malwareProtection:              '§164.308(a)(5)(ii)(B)',
    dataBackupPlan:                 '§164.308(a)(7)(ii)(A)',
    disasterRecoveryPlan:           '§164.308(a)(7)(ii)(B)',
    physicalAccessControls:         '§164.310(a)(1)',
    facilitySecurity:               '§164.310(a)(2)(ii)',
    securityMaintenance:            '§164.310(a)(2)(iii)',
    workstationLocation:            '§164.310(b)',
    workstationSpecifications:      '§164.310(c)',
    disposalProcedures:             '§164.310(d)(2)(i)',
    mediaTracking:                  '§164.310(d)(2)(iii)',
    backupCopies:                   '§164.310(d)(2)(iv)',
    uniqueUserIdentification:       '§164.312(a)(2)(i)',
    emergencyAccessProcedures:      '§164.312(a)(2)(ii)',
    automaticLogoff:                '§164.312(a)(2)(iii)',
    dataEncryption:                 '§164.312(a)(2)(iv)',
    auditControls:                  '§164.312(b)',
    integrityControls:              '§164.312(c)(1)',
    transmissionEncryption:         '§164.312(e)(2)(ii)',
    transmissionIntegrityControls:  '§164.312(e)(2)(i)',
  };

  // Helper to render a multiple choice answer set
  const renderAnswerOptions = (question, answerSet, handleChange) => (
    <div>
      {CITATIONS[question] && (
        <span className="inline-block text-xs font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded mb-2">{CITATIONS[question]}</span>
      )}
      <div className="flex flex-wrap space-x-2">
      <label className={`flex items-center p-2 rounded ${answerSet[question] === 1 ? 'bg-green-100' : ''}`}>
        <input 
          type="radio" 
          name={question} 
          value={1} 
          checked={answerSet[question] === 1} 
          onChange={() => handleChange(question, 1)}
          className="mr-2"
        />
        Yes, comprehensive
      </label>
      <label className={`flex items-center p-2 rounded ${answerSet[question] === 0.5 ? 'bg-yellow-100' : ''}`}>
        <input 
          type="radio" 
          name={question} 
          value={0.5} 
          checked={answerSet[question] === 0.5} 
          onChange={() => handleChange(question, 0.5)}
          className="mr-2"
        />
        Yes, partial
      </label>
      <label className={`flex items-center p-2 rounded ${answerSet[question] === 0 ? 'bg-red-100' : ''}`}>
        <input 
          type="radio" 
          name={question} 
          value={0} 
          checked={answerSet[question] === 0} 
          onChange={() => handleChange(question, 0)}
          className="mr-2"
        />
        No
      </label>
      <label className={`flex items-center p-2 rounded ${answerSet[question] === -1 ? 'bg-gray-100' : ''}`}>
        <input
          type="radio"
          name={question}
          value={-1}
          checked={answerSet[question] === -1}
          onChange={() => handleChange(question, -1)}
          className="mr-2"
        />
        Not applicable
      </label>
    </div>
    </div>
  );

  // Get relevant HIPAA requirements for a section
  const getRelevantRequirements = (section) => {
    const hipaaReqs = regulatoryMatrix.hipaa.requirements;
    
    // Return requirements relevant to the current section
    if (section === 'administrative') {
      return hipaaReqs.filter(req => 
        req.citation.includes('308') || 
        req.name.toLowerCase().includes('management') || 
        req.name.toLowerCase().includes('training')
      );
    } else if (section === 'physical') {
      return hipaaReqs.filter(req => 
        req.citation.includes('310') || 
        req.name.toLowerCase().includes('device') || 
        req.name.toLowerCase().includes('physical')
      );
    } else if (section === 'technical') {
      return hipaaReqs.filter(req => 
        req.citation.includes('312') || 
        req.name.toLowerCase().includes('integrity') || 
        req.name.toLowerCase().includes('access') ||
        req.name.toLowerCase().includes('transmission')
      );
    }
    
    return [];
  };

  return (
    <div className="bg-white p-6 rounded shadow mb-8">
      <h2 className="text-xl font-semibold mb-4">Safeguards Assessment</h2>
      
      <div className="flex mb-6 border-b">
        <button 
          className={`py-2 px-4 mr-2 ${activeSection === 'administrative' ? 'border-b-2 border-blue-500 font-medium' : 'text-gray-500'}`}
          onClick={() => setActiveSection('administrative')}
        >
          Administrative
          <span className={`ml-2 text-xs px-2 py-1 rounded ${getScoreColor(adminScore)}`} title={getScoreTooltip(adminScore)}>
            {adminScore.toFixed(1)}
          </span>
        </button>
        <button 
          className={`py-2 px-4 mr-2 ${activeSection === 'physical' ? 'border-b-2 border-blue-500 font-medium' : 'text-gray-500'}`}
          onClick={() => setActiveSection('physical')}
        >
          Physical
          <span className={`ml-2 text-xs px-2 py-1 rounded ${getScoreColor(physicalScore)}`} title={getScoreTooltip(physicalScore)}>
            {physicalScore.toFixed(1)}
          </span>
        </button>
        <button 
          className={`py-2 px-4 ${activeSection === 'technical' ? 'border-b-2 border-blue-500 font-medium' : 'text-gray-500'}`}
          onClick={() => setActiveSection('technical')}
        >
          Technical
          <span className={`ml-2 text-xs px-2 py-1 rounded ${getScoreColor(technicalScore)}`} title={getScoreTooltip(technicalScore)}>
            {technicalScore.toFixed(1)}
          </span>
        </button>
      </div>
      
      {/* Administrative Safeguards */}
      {activeSection === 'administrative' && (
        <div>
          <div className="mb-4 bg-blue-50 p-3 rounded">
            <p className="text-sm">
              Administrative safeguards are administrative actions, policies, and procedures to manage the selection, 
              development, implementation, and maintenance of security measures to protect ePHI, and to manage the 
              conduct of the covered entity's or business associate's workforce in relation to the protection of that information.
            </p>
          </div>
          
          <div className="mb-6">
            <h3 className="font-medium mb-2">Security Management (§164.308(a)(1))</h3>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Is there a formal process for conducting security risk assessments for this medical device?</p>
              {renderAnswerOptions('securityRiskProcess', adminAnswers, handleAdminAnswerChange)}
            </div>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Is there a documented risk management plan addressing identified risks for this device?</p>
              {renderAnswerOptions('riskManagementPlan', adminAnswers, handleAdminAnswerChange)}
            </div>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Are security incidents involving this device formally documented and reviewed?</p>
              {renderAnswerOptions('securityIncidentDocumentation', adminAnswers, handleAdminAnswerChange)}
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="font-medium mb-2">Workforce Security & Access Management</h3>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Are authorization and/or supervision procedures established for staff using this device?</p>
              {renderAnswerOptions('authorizationProcedures', adminAnswers, handleAdminAnswerChange)}
            </div>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Is there a defined process for terminating access to this device when no longer appropriate?</p>
              {renderAnswerOptions('accessTerminationProcess', adminAnswers, handleAdminAnswerChange)}
            </div>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Has an access authorization process been established for this device?</p>
              {renderAnswerOptions('accessAuthorizationProcess', adminAnswers, handleAdminAnswerChange)}
            </div>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Is access to this device based on role or job function (role-based access)?</p>
              {renderAnswerOptions('roleBasedAccess', adminAnswers, handleAdminAnswerChange)}
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="font-medium mb-2">Security Awareness & Contingency Planning</h3>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Has staff been trained on the security features and requirements for this device?</p>
              {renderAnswerOptions('staffTraining', adminAnswers, handleAdminAnswerChange)}
            </div>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Are procedures in place to protect against malware for this device?</p>
              {renderAnswerOptions('malwareProtection', adminAnswers, handleAdminAnswerChange)}
            </div>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Is there a data backup plan for this device?</p>
              {renderAnswerOptions('dataBackupPlan', adminAnswers, handleAdminAnswerChange)}
            </div>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Is there a disaster recovery plan addressing this device?</p>
              {renderAnswerOptions('disasterRecoveryPlan', adminAnswers, handleAdminAnswerChange)}
            </div>
          </div>
          
          <div className="mt-6 bg-gray-50 p-4 rounded">
            <h3 className="font-medium mb-2">Related HIPAA Requirements</h3>
            <div className="space-y-2">
              {getRelevantRequirements('administrative').map(req => (
                <div key={req.id} className="border rounded p-3">
                  <div className="flex justify-between cursor-pointer" onClick={() => setExpandedRequirement(expandedRequirement === req.id ? null : req.id)}>
                    <span className="font-medium">{req.name}</span>
                    <span className="text-sm text-gray-500">{req.citation}</span>
                  </div>
                  {expandedRequirement === req.id && (
                    <div className="mt-2 text-sm text-gray-700">
                      <p className="mb-1">{req.description}</p>
                      <p className="mt-2 text-blue-600">{req.deviceRelevance}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Physical Safeguards */}
      {activeSection === 'physical' && (
        <div>
          <div className="mb-4 bg-blue-50 p-3 rounded">
            <p className="text-sm">
              Physical safeguards are physical measures, policies, and procedures to protect electronic information 
              systems and related buildings and equipment from natural and environmental hazards, and unauthorized intrusion.
            </p>
          </div>
          
          <div className="mb-6">
            <h3 className="font-medium mb-2">Facility Access Controls (§164.310(a)(1))</h3>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Are there physical safeguards controlling access to this device?</p>
              {renderAnswerOptions('physicalAccessControls', physicalAnswers, handlePhysicalAnswerChange)}
            </div>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Are facilities housing this device protected with appropriate physical security measures (e.g., locks, cameras, access control)?</p>
              {renderAnswerOptions('facilitySecurity', physicalAnswers, handlePhysicalAnswerChange)}
            </div>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Are maintenance records kept for physical security systems protecting this device?</p>
              {renderAnswerOptions('securityMaintenance', physicalAnswers, handlePhysicalAnswerChange)}
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="font-medium mb-2">Workstation & Device Security (§164.310(b)-(c))</h3>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Are workstations used to access this device located in secure areas?</p>
              {renderAnswerOptions('workstationLocation', physicalAnswers, handlePhysicalAnswerChange)}
            </div>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Are there policies specifying proper functions and physical attributes for workstations that access this device?</p>
              {renderAnswerOptions('workstationSpecifications', physicalAnswers, handlePhysicalAnswerChange)}
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="font-medium mb-2">Device and Media Controls (§164.310(d)(1))</h3>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Is there a documented procedure for disposal of ePHI from this device?</p>
              {renderAnswerOptions('disposalProcedures', physicalAnswers, handlePhysicalAnswerChange)}
            </div>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Are media containing ePHI from this device tracked and managed?</p>
              {renderAnswerOptions('mediaTracking', physicalAnswers, handlePhysicalAnswerChange)}
            </div>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Are there backup copies of ePHI maintained for this device?</p>
              {renderAnswerOptions('backupCopies', physicalAnswers, handlePhysicalAnswerChange)}
            </div>
          </div>
          
          <div className="mt-6 bg-gray-50 p-4 rounded">
            <h3 className="font-medium mb-2">Related HIPAA Requirements</h3>
            <div className="space-y-2">
              {getRelevantRequirements('physical').map(req => (
                <div key={req.id} className="border rounded p-3">
                  <div className="flex justify-between cursor-pointer" onClick={() => setExpandedRequirement(expandedRequirement === req.id ? null : req.id)}>
                    <span className="font-medium">{req.name}</span>
                    <span className="text-sm text-gray-500">{req.citation}</span>
                  </div>
                  {expandedRequirement === req.id && (
                    <div className="mt-2 text-sm text-gray-700">
                      <p className="mb-1">{req.description}</p>
                      <p className="mt-2 text-blue-600">{req.deviceRelevance}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Technical Safeguards */}
      {activeSection === 'technical' && (
        <div>
          <div className="mb-4 bg-blue-50 p-3 rounded">
            <p className="text-sm">
              Technical safeguards are the technology and the policies and procedures for its use that protect 
              electronic protected health information and control access to it.
            </p>
          </div>
          
          <div className="mb-6">
            <h3 className="font-medium mb-2">Access Control (§164.312(a)(1))</h3>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Does this device require unique user identification?</p>
              {renderAnswerOptions('uniqueUserIdentification', technicalAnswers, handleTechnicalAnswerChange)}
            </div>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Does this device support emergency access procedures?</p>
              {renderAnswerOptions('emergencyAccessProcedures', technicalAnswers, handleTechnicalAnswerChange)}
            </div>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Does this device automatically log off users after a period of inactivity?</p>
              {renderAnswerOptions('automaticLogoff', technicalAnswers, handleTechnicalAnswerChange)}
            </div>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Is data encryption implemented on this device?</p>
              {renderAnswerOptions('dataEncryption', technicalAnswers, handleTechnicalAnswerChange)}
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="font-medium mb-2">Audit and Integrity Controls (§164.312(b)-(c))</h3>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Does this device record and examine activity (audit logs)?</p>
              {renderAnswerOptions('auditControls', technicalAnswers, handleTechnicalAnswerChange)}
            </div>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Are there mechanisms to ensure ePHI is not improperly altered or destroyed on this device?</p>
              {renderAnswerOptions('integrityControls', technicalAnswers, handleTechnicalAnswerChange)}
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="font-medium mb-2">Transmission Security (§164.312(e)(1))</h3>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Is encryption used when transmitting ePHI from this device?</p>
              {renderAnswerOptions('transmissionEncryption', technicalAnswers, handleTechnicalAnswerChange)}
            </div>
            
            <div className="mb-3 p-3 rounded border">
              <p className="mb-2">Are integrity controls in place for ePHI transmitted from this device?</p>
              {renderAnswerOptions('transmissionIntegrityControls', technicalAnswers, handleTechnicalAnswerChange)}
            </div>
          </div>
          
          <div className="mt-6 bg-gray-50 p-4 rounded">
            <h3 className="font-medium mb-2">Related HIPAA Requirements</h3>
            <div className="space-y-2">
              {getRelevantRequirements('technical').map(req => (
                <div key={req.id} className="border rounded p-3">
                  <div className="flex justify-between cursor-pointer" onClick={() => setExpandedRequirement(expandedRequirement === req.id ? null : req.id)}>
                    <span className="font-medium">{req.name}</span>
                    <span className="text-sm text-gray-500">{req.citation}</span>
                  </div>
                  {expandedRequirement === req.id && (
                    <div className="mt-2 text-sm text-gray-700">
                      <p className="mb-1">{req.description}</p>
                      <p className="mt-2 text-blue-600">{req.deviceRelevance}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      <div className="mt-6 p-4 bg-gray-50 rounded">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium">Overall Safeguards Score</h3>
            <p className="text-sm text-gray-600">Average of administrative, physical, and technical controls</p>
          </div>
          <div className={`px-4 py-2 rounded text-white font-bold ${
            (adminScore + physicalScore + technicalScore) / 3 >= 8 ? 'bg-green-500' : 
            (adminScore + physicalScore + technicalScore) / 3 >= 6 ? 'bg-blue-500' : 
            (adminScore + physicalScore + technicalScore) / 3 >= 4 ? 'bg-yellow-500' : 'bg-red-500'
          }`}>
            {((adminScore + physicalScore + technicalScore) / 3).toFixed(1)}/10
          </div>
        </div>
      </div>
    </div>
  );
}